<script lang="ts" setup>
import { Button, Hero, Icon } from "@/components/Ui";
import { onEvent, openLink } from "@/helpers/events";
import { type TCard } from "@/config/cards";
import { useRouter } from "vue-router";

const router = useRouter();

interface Props {
  label?: string;
  labelIcon?: string;
  card: TCard;
  buttonEvent?: string;
  buttonText?: string;
  isTariffShown?: boolean;
  to?: "private" | "card-create";
}

const { t } = useI18n();
const props = withDefaults(defineProps<Props>(), {
  to: "private",
});

const navigate = () => {
  if (props.to === "private") {
    router.push("/private");
  } else {
    openLink("https://app.pst.net/app/create");
  }
};
</script>
<template>
  <Hero
    :label="props.label"
    :label-icon="props.labelIcon"
    class="container"
  >
    <div :class="$style.head">
      <h1 :class="`${$style[`title`]} ${$style[`title-${$i18n.locale}`]}`">
        <slot name="title" />
      </h1>
      <p v-if="$slots.description">
        <slot name="description" />
      </p>
    </div>
    <ul
      v-if="isTariffShown"
      class="flex gap-5 max-w-[440px] w-full items-center justify-between"
    >
      <li class="text-center">
        <p class="text-2xl font-medium leading-8">{{ card.transaction_fee }}</p>
        <p class="text-neutral-700 text-base">
          {{ t("cards.transaction_fee") }}
        </p>
      </li>
      <li class="text-center">
        <p class="text-2xl font-medium leading-8">{{ card.withdraw_fee }}</p>
        <p class="text-neutral-700 text-base">
          {{ t("cards.withdraw_fee") }}
        </p>
      </li>
    </ul>
    <div :class="$style[`card`]">
      <Icon
        v-if="props.card.svg"
        :name="`pst-${props.card.svg}`"
        path="cards"
      />
      <nuxt-img
        v-else
        :src="`/img/cards/${props.card.image}`"
        alt=""
        format="webp"
      />
      <Button
        :class="$style[`button`]"
        :title="props.buttonText ?? t('btn.issueCard')"
        color="orange"
        size="custom"
        @click="
          onEvent(props.buttonEvent || 'create account started', navigate)
        "
      />
    </div>
  </Hero>
</template>
<style lang="scss" module>
.head {
  @apply flex flex-col gap-[10px] text-center;

  & p {
    @apply text-[15px] text-neutral-700;
  }
}

.title {
  @apply mb-0 font-black leading-none;
  @apply text-[40px];
  &-en,
  &-id,
  &-da,
  &-et,
  &-hu,
  &-lt,
  &-nb,
  &-ro,
  &-sl,
  &-cs,
  &-el,
  &-fi,
  &-lv,
  &-nl,
  &-sk,
  &-sv,
  &-he,
  &-be,
  &-bg,
  &-pt,
  &-ua,
  &-tr,
  &-zh,
  &-it,
  &-fr,
  &-es,
  &-de,
  &-ja,
  &-th,
  &-pl,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw {
    @apply text-[40px];
  }

  &-ru {
    @apply text-[30px] xxs:text-[32px] 414:text-[40px];
  }

  &-ja,
  &-tr,
  &-zh {
    @apply leading-[1.2];
  }
}

.tariff {
  @apply flex flex-row gap-5 lg:w-fit lg:max-w-full justify-center;

  & > li {
    @apply flex flex-col w-1/3 lg:w-[140px] text-center;

    & span {
      &:first-child {
        @apply text-[25px] text-neutral-900;
      }

      &:last-child {
        @apply text-[12px] xxs:text-[14px] xs:text-[16px] text-neutral-700;
      }
    }
  }

  &-old {
    @apply hidden lg:inline-flex opacity-60 not-italic line-through px-2;
  }
}

.card {
  @apply flex flex-col items-center gap-[10px] w-full max-w-[440px];

  & img,
  & svg {
    @apply h-full w-full;
  }
}

.button {
  @apply text-[15px] w-full h-12;
}
</style>
